(function(){
  /**
   * This is a component to wrap content which can be scrolled left/right with just arrow buttons but not a full scroll bar.
   *
   * It is designed to operate in two basic modes: fixed size and dynamic size.
   *
   * In fixed size mode, the content subject to scroll is sized by the browser to be just large enough to contain all
   * elements.  In dynamic size mode, the content width is set by the HorizontalScroll component itself.  The content
   * will stretch and shrink horizontally to fill the viewport, down to a minimum size.  Only when the viewport becomes
   * smaller than the specified size do the scroll arrows become active.
   *
   * Fixed size mode is the default.  To enable dynamic size the edxMinContainerWidth parameter is set.
   *
   * The content should not be a block element if fixed size mode is used.
   *
   * An API is exported for programatically interacting with the HorizontalScroll component.  This is obtained using
   * the edxApi parameter.  The exported interface is bound to the variable 'api' in the callback.
   *
   * Parameters:
   *
   * edxStepSize - The number of pixels to scroll left or right when scroll arrows are clicked.  This can be changed
   * dynamically.
   *
   * edxMinContainerWidth - Specify the minimum width, in pixels, of the scrollable content.  Setting this parameter
   * will cause the scrollable content to be set to the same size as the viewport unless the viewport width is less
   * than this parameter in which case the scrollable content width is set to this minimum value.  Omitting this
   * parameter will cause the content to be sized by the browser.  This value can be changed dynamically.
   *
   * edxApi - Callback for getting access to exported API.
   *
   * Usage:
   * <edx-horizontal-scroll
   *   edx-step-size="40"
   *   edx-min-container-width="1000"
   *   edx-api="vm.hscroll=api">
   *
   *   <client content>
   *
   * </edx-horizontal-scroll>
   *
   * Exported Apis:
   *
   * scrollTo(left,right) - Scrolls view so the specified segment of content is visible.
   *
   * Implementation Notes:
   *
   * There are four key HTML elements that we work with.  The left and right arrows, the viewport and the container.
   * The arrows are self explanatory.  The viewport element is sized to file 100% of the available width (less the
   * arrow buttons).  This element clips any overflow of its children.  We move the content around relative to this
   * element to view different parts of the content.  The container element is a direct child of the viewport.  It is
   * sized as large as necessary to contain all of the content.  This element has relative positioning.  We move it
   * around to make different parts of the content visible within the viewport element.
   */
  angular.module('edxComponents')
    .component('edxHorizontalScroll', {
      transclude: true,
      templateUrl: '/resources/components/standards-completion/horizontal-scroll.component/horizontal-scroll.component.html',
      controller: [HorizontalScrollController],
      controllerAs: 'vm',
      bindings: {
        stepSize: '<edxStepSize',
        minContainerWidth: '<edxMinContainerWidth',
        api: '&edxApi'
      }
    });

  function HorizontalScrollController() {
    var vm = this;

    vm.onResizeViewport = onResizeViewport;
    vm.onResizeContainer = onResizeContainer;
    vm.onLeftArrowClick = onLeftArrowClick;
    vm.onRightArrowClick = onRightArrowClick;
    vm.canScrollLeft = canScrollLeft;
    vm.canScrollRight = canScrollRight;
    vm.computeContainerStyle = computeContainerStyle;
    vm.scrollTo = hScrollTo;
    vm.getContainerWidth = getContainerWidth;
    vm.getCurrentOffset = getCurrentOffset;

    // Export API
    var _export = {
      scrollTo: hScrollTo,
      getContainerWidth: getContainerWidth,
      getCurrentOffset: getCurrentOffset
    };
    if (vm.api) {
      vm.api({api: _export});
    }

    // Set a default step size
    if (!vm.stepSize) {
      vm.stepSize = 20;
    }

    var viewportWidth = 0; // Measured width of viewport
    var containerWidth = 0; // Measured width of container
    var hOffset = 0; // Distance from left edge of container to left edge of viewport
    var scrollToDelayLeft, scrollToDelayRight;

    /**
     * Scroll the viewport so that the segment of content is visible.
     *
     * In some cases, it is necessary call scrollTo before we know the size of the container, because the browser
     * hasn't rendered it yet.  Normally, the scrollTo will have no affect because the offset will get clipped to
     * 0, the current width of the container.  To work around this, setting the 'delay' parameter to true will cause
     * the scroll to operation to be delayed until the next resize of the container.
     *
     * @param left Left offset of area to be visible
     * @param right Right offset of area to be visible
     * @param delay True to delay scrollTo operation until next container resize
     */
    function hScrollTo(left, right, delay) {
      if (right < left) {
        var tmp = left;
        left = right;
        right = tmp;
      }

      if (delay) {
        scrollToDelayLeft = left;
        scrollToDelayRight = right;
      } else {
        if (right - left > viewportWidth) {
          if (_isLeftOfViewport(left) && _isInViewport(right)) {
            _setHOffset(right - viewportWidth);
          } else {
            _setHOffset(left);
          }
        } else {
          if (_isLeftOfViewport(left)) {
            _setHOffset(left);
          } else if (_isRightOfViewport(right)) {
            _setHOffset(right - viewportWidth);
          }
        }
      }
    }

    /**
     * Determine if an offset falls to the left of the viewport.
     *
     * @param cx The offset to test
     * @return {boolean}
     * @private
     */
    function _isLeftOfViewport(cx) {
      return _comparePointToViewport(cx) < 0;
    }

    /**
     * Determine if an offset falls to the right of the viewport.
     *
     * @param cx The offset to test
     * @return {boolean}
     * @private
     */
    function _isRightOfViewport(cx) {
      return _comparePointToViewport(cx) > 0;
    }

    /**
     * Determine if an offset falls within the viewport.
     *
     * @param cx The offset to test
     * @return {boolean}
     * @private
     */
    function _isInViewport(cx) {
      return _comparePointToViewport(cx) === 0;
    }

    /**
     * Compares an offset to the viewport bounds.
     *
     * @param cx The offset to test
     * @return {number} -1 left of viewport, 0 within viewport, 1 right of viewport
     * @private
     */
    function _comparePointToViewport(cx) {
      if (cx < hOffset) {
        return -1;
      }

      if (cx >= hOffset + viewportWidth) {
        return 1;
      }

      return 0;
    }

    /**
     * Dynamically compute CSS style to be applied to the container element.
     *
     * @return {{left: string, display, width}}
     */
    function computeContainerStyle() {
      var style = {
        left: _computeOffsetPx(),
        display: _containerDisplayStyle()
      };

      if (_isContainerDynamicallySized()) {
        style.width = _computeContainerWidth();
      }
      return style;
    }

    /**
     * Dynamically compute the CSS width attribute of the container element.
     *
     * @return {*}
     * @private
     */
    function _computeContainerWidth() {
      return Math.max(viewportWidth, vm.minContainerWidth) + 'px';
    }

    /**
     * Returns the current offset in CSS compatible format
     * @return {string}
     */
    function _computeOffsetPx() {
      return (-hOffset) + 'px';
    }

    /**
     * Handle change in viewport size.
     *
     * @param cx Viewport width in px
     * @param cy Viewport height in px
     */
    function onResizeViewport(cx, cy) {
      viewportWidth = cx;
      _setHOffset(hOffset);
    }

    /**
     * Handle change in container size.
     *
     * @param cx Container width in px
     * @param cy Container height in px
     */
    function onResizeContainer(cx, cy) {
      containerWidth = cx;
      if (scrollToDelayLeft && scrollToDelayRight) {
        hScrollTo(scrollToDelayLeft, scrollToDelayRight);
        scrollToDelayLeft = scrollToDelayRight = undefined;
      } else {
        _setHOffset(hOffset);
      }
    }

    /**
     * Handle click on left arrow.
     */
    function onLeftArrowClick() {
      _setHOffset(hOffset - vm.stepSize);
    }

    /**
     * Handle click on right arrow.
     */
    function onRightArrowClick() {
      _setHOffset(hOffset + vm.stepSize);
    }

    /**
     * Determine if the left arrow is enabled.
     */
    function canScrollLeft() {
      return hOffset > _minOffset();
    }

    /**
     * Determine if the right arrow is enabled.
     */
    function canScrollRight() {
      return hOffset < _maxOffset();
    }

    /**
     * Gets the current width of the container.
     *
     * @return {number}
     */
    function getContainerWidth() {
      return containerWidth;
    }

    /**
     * Gets the current offset.
     *
     * @return {number}
     */
    function getCurrentOffset() {
      return hOffset;
    }

    /**
     * Determine if the container is dynamically sized by the horizontal scroll component.
     *
     * Dynamically sized content will be resized by the hscroll component to stretch and shrink with the size
     * of the viewport, down to a specified minimum width.
     *
     * @return {boolean}
     * @private
     */
    function _isContainerDynamicallySized() {
      return !!vm.minContainerWidth;
    }

    /**
     * Determine the CSS display style for the container element.
     *
     * @return {*}
     * @private
     */
    function _containerDisplayStyle() {
      if (_isContainerDynamicallySized()) {
        return 'inline-block';
      } else {
        return 'inline';
      }
    }

    /**
     * Compute the minimum allowable offset value.
     *
     * @return {number}
     * @private
     */
    function _minOffset() {
      return 0;
    }

    /**
     * Compute the maximum allowable offset value.
     * @return {number}
     * @private
     */
    function _maxOffset() {
      if (containerWidth > viewportWidth) {
        return containerWidth - viewportWidth;
      } else {
        return 0;
      }
    }

    /**
     * Set the offset value, capped within bounds.
     *
     * Sets offset to the specified value if within permissible range.  Values outside the permissible range
     * are modified to the nearest allowable value.
     *
     * @param offset
     * @private
     */
    function _setHOffset(offset) {
      if (offset < _minOffset()) {
        hOffset = _minOffset();
      } else if (offset > _maxOffset()) {
        hOffset = _maxOffset();
      } else {
        hOffset = offset;
      }
    }
  }
})();